import { useTranslation } from 'react-i18next';
import { Mail, Headphones, MessageCircle, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, { PageHero, LightSection, SectionHead } from '@/components/PageShell';
import { openSupportChat } from '@/lib/chat';

const CONTACT_DESCRIPTION =
  "Get in touch with Sport Fund. Questions about fundraising, donations, or your account — we're here to help.";

export default function Contact() {
  usePageMeta('Contact', CONTACT_DESCRIPTION);
  const { t } = useTranslation();
  const openChat = openSupportChat;

  // mailto: links give no in-page feedback on click -- indistinguishable from a
  // dead button when the visitor has no default mail client configured (a real
  // rageclick burst on this exact page, 2026-09-03, PostHog confirmed). Copying
  // the address gives them a usable fallback even if the mailto: does nothing.
  const copyEmailFallback = (email: string) => {
    navigator.clipboard?.writeText(email).then(
      () => toast.success(`${email} copied to clipboard`),
      () => { /* clipboard unavailable -- mailto: still attempted via href */ }
    );
  };

  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Contact us"
          title={
            <>
              We&apos;re real people.{' '}
              <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">Reach out any time.</span>
            </>
          }
          lead={t('contactPage.hero.subtitle')}
          size="sm"
        />
      }
    >
      <LightSection>
        <div className="grid lg:grid-cols-3 gap-5">
          <a
            href={`mailto:${t('contactPage.generalQuestions.email')}`}
            onClick={() => copyEmailFallback(t('contactPage.generalQuestions.email'))}
            className="group rounded-3xl bg-white border border-slate-200/70 p-8 hover:border-slate-300 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-xl"
          >
            <div className="h-12 w-12 rounded-xl bg-blue-50 text-blue-700 grid place-items-center">
              <Mail className="h-6 w-6" />
            </div>
            <h3 className="mt-6 text-xl font-bold tracking-tight text-slate-900">{t('contactPage.generalQuestions.title')}</h3>
            <p className="mt-3 text-slate-600 leading-relaxed text-sm">{t('contactPage.generalQuestions.description')}</p>
            <div className="mt-6 inline-flex items-center gap-2 text-blue-700 font-semibold">
              {t('contactPage.generalQuestions.email')}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </div>
          </a>

          <a
            href={`mailto:${t('contactPage.technicalSupport.email')}`}
            onClick={() => copyEmailFallback(t('contactPage.technicalSupport.email'))}
            className="group rounded-3xl bg-white border border-slate-200/70 p-8 hover:border-slate-300 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-xl"
          >
            <div className="h-12 w-12 rounded-xl bg-yellow-50 text-yellow-700 grid place-items-center">
              <Headphones className="h-6 w-6" />
            </div>
            <h3 className="mt-6 text-xl font-bold tracking-tight text-slate-900">{t('contactPage.technicalSupport.title')}</h3>
            <p className="mt-3 text-slate-600 leading-relaxed text-sm">{t('contactPage.technicalSupport.description')}</p>
            <div className="mt-6 inline-flex items-center gap-2 text-yellow-700 font-semibold">
              {t('contactPage.technicalSupport.email')}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </div>
          </a>

          <button
            type="button"
            onClick={openChat}
            className="group text-left rounded-3xl bg-[#070b18] text-white border border-white/10 p-8 hover:-translate-y-1 transition-all duration-300 shadow-xl shadow-black/20 hover:shadow-2xl relative overflow-hidden"
          >
            <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-yellow-400/20 blur-3xl" aria-hidden />
            <div className="relative">
              <div className="h-12 w-12 rounded-xl bg-yellow-400/15 border border-yellow-400/30 text-yellow-300 grid place-items-center">
                <MessageCircle className="h-6 w-6" />
              </div>
              <h3 className="mt-6 text-xl font-bold tracking-tight text-white">{t('contactPage.liveChat.title')}</h3>
              <p className="mt-3 text-slate-300 leading-relaxed text-sm">{t('contactPage.liveChat.description')}</p>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-yellow-400 text-slate-900 px-5 py-2.5 font-semibold text-sm">
                {t('contactPage.liveChat.button')}
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </button>
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 rounded-3xl border border-slate-200/70 bg-slate-50 p-8">
            <SectionHead eyebrow="Our mission" title={t('contactPage.mission.title')} />
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">{t('contactPage.mission.description')}</p>
          </div>
          <div className="rounded-3xl border border-slate-200/70 bg-white p-8">
            <div className="h-12 w-12 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
              <Clock className="h-6 w-6" />
            </div>
            <h3 className="mt-6 text-lg font-bold text-slate-900">{t('contactPage.responseTime.title')}</h3>
            <p className="mt-2 text-slate-600 leading-relaxed text-sm">{t('contactPage.responseTime.description')}</p>
          </div>
        </div>
      </LightSection>
    </PageShell>
  );
}
